import { DomSanitizer } from "@angular/platform-browser";
import {
  ApplicationConfig,
  createEnvironmentInjector,
  EnvironmentInjector,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  signal
} from "@angular/core";
import { Router, NavigationEnd, provideRouter, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { DatePipe, DecimalPipe, registerLocaleData } from "@angular/common";
import { provideTransloco, TranslocoService } from "@jsverse/transloco";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from "@angular/material/tooltip";
import { MatIconRegistry } from "@angular/material/icon";
import { registerCustomIcons } from "@on-geo/accumate-ui/icons";
import { HeaderLocationService } from "@services/header-location.service";
import { HeaderNavigationService } from "@services/header-navigation.service";
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MAT_DATE_FNS_FORMATS, provideDateFnsAdapter } from "@angular/material-date-fns-adapter";
import { de } from "date-fns/locale";
import { TranslocoHttpLoader } from "./transloco-loader";
import { provideTranslocoLocale, TranslocoLocaleService } from "@jsverse/transloco-locale";
import { switchMap, tap, filter, take, EMPTY } from "rxjs";
import { InitService } from "@services/init.service";
import { OptionService } from "./modules/option/option.service";
import { provideTranslocoMessageformat } from "@jsverse/transloco-messageformat";
import { provideNgxMask } from "ngx-mask";
import { createNgxMaskConfig, MaskService } from "@services/mask.service";
import { httpRequestInterceptor } from "./modules/core/interceptor/httprequestinterceptor";
import { avmQuotaInterceptor } from "./modules/core/interceptor/avm-quota.interceptor";
import { routes } from "./app.routes";
import { ConfigService } from "@services/config.service";
import localDe from "@angular/common/locales/de";
import localEn from "@angular/common/locales/en";
import localFr from "@angular/common/locales/fr";
import localNl from "@angular/common/locales/nl";
import localEs from "@angular/common/locales/es";
import { provideAnimations } from "@angular/platform-browser/animations";
import { FormattingService } from "@services/formatting.service";
import { provideTranslocoPreloadLangs } from "@jsverse/transloco-preload-langs";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";
import { RealEstateMapService } from "./modules/real-estate/services/real-estate-map.service";
import { AbstractRealEstateApiService } from "./modules/real-estate/abstract-base/abstract-real-estate-api.service";
import { MixedApiService } from "./modules/real-estate/inherit-real-estate-api-services/mixed-api.service";
import { MixedRealEstateService } from "./modules/real-estate/inherit-real-estate-service/mixed-real-estate.service";
import { ESTATE_TYPE } from "./modules/real-estate/tokens/estate-type.token";
import { EstateType } from "./shared/contract/Enum/portfolio.enum";

const availableLanguages = ["de-DE", "en-GB", "es-CL", "es-ES", "fr-BE", "nl-BE"];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: "always" })),
    provideAnimations(),
    provideAppInitializer(() => registerCustomIcons(inject(MatIconRegistry), inject(DomSanitizer))),
    provideTranslocoPreloadLangs(availableLanguages),
    provideTranslocoLocale({
      defaultCurrency: null,
      localeConfig: {
        global: {
          decimal: { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        }
      }
    }),
    provideTransloco({
      config: {
        defaultLang: "de-DE",
        availableLangs: availableLanguages,
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: "dynamic" }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 0,
        hideDelay: 0,
        disableTooltipInteractivity: false
      }
    },
    provideHttpClient(withInterceptors([httpRequestInterceptor, avmQuotaInterceptor])),
    provideDateFnsAdapter(),
    provideTranslocoMessageformat(),
    { provide: MAT_DATE_LOCALE, useValue: de },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
    {
      provide: LOCALE_ID,
      useFactory: () => inject(OptionService).locale() || "de-DE"
    },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    {
      provide: MixedRealEstateService,
      useFactory: (parentInjector: EnvironmentInjector) =>
        createEnvironmentInjector(
          [
            MixedRealEstateService,
            { provide: ESTATE_TYPE, useFactory: () => signal(EstateType.Mixed) },
            { provide: AbstractRealEstateApiService, useExisting: MixedApiService }
          ],
          parentInjector
        ).get(MixedRealEstateService),
      deps: [EnvironmentInjector]
    },
    HeaderLocationService,
    HeaderNavigationService,
    provideAppInitializer(preloadInitAndLanguage),
    provideAppInitializer(initializeRootRedirect),
    {
      provide: "NGX_MASK_CONFIG",
      useFactory: createNgxMaskConfig,
      deps: [MaskService]
    },
    provideNgxMask(),
    ConfigService,
    DecimalPipe,
    DatePipe,
    FormattingService,
    provideAppInitializer(() => {
      inject(RealEstateMapService);
    })
  ]
};

export function preloadInitAndLanguage() {
  const optionService = inject(OptionService);
  const initService = inject(InitService);
  const translocoService = inject(TranslocoService);
  const translocoLocaleService = inject(TranslocoLocaleService);

  // Register all locale data to ensure they are available
  registerLocaleData(localDe, "de-DE");
  registerLocaleData(localEn, "en-GB");
  registerLocaleData(localFr, "fr-BE");
  registerLocaleData(localNl, "nl-BE");
  registerLocaleData(localEs, "es-ES");
  registerLocaleData(localEs, "es-CL");

  return initService.getInitData().pipe(
    tap(initResponse => (initService.InitData = initResponse)),
    switchMap(() => optionService.preloadLanguage(initService, translocoService, translocoLocaleService))
  );
}

export function initializeRootRedirect() {
  const router = inject(Router);
  const currentUrl = globalThis.location.pathname.toLowerCase();
  const validRoots = routes.map(r => "/" + (r.path || "")).map(p => p.toLowerCase());

  const isValid = validRoots.some(root => currentUrl.startsWith(root));

  if (isValid) {
    return EMPTY;
  }

  router.navigate(["/"]);

  return router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    take(1)
  );
}
